// Import react elements
import React, { useState, useEffect } from 'react';

// Import hooks
import { usePersistentFilters } from 'global/hooks/usePersistentFilters';


// Import components
import CustomisableTable from 'global/components/CustomisableTable';
import CreateButton from "global/components/CreateButton";
import CreateToggleFilters from 'global/components/CreateToggleFilters';
import CreateDateFilterPickers from 'global/components/CreateDateFilterPickers';

// Import utilities
import { 
    formatDateFromMidnightString,
    formatDateToMidnightString,
    getDateDaysAgo,
    getMonthToDateStartDate,
    getYearToDateStartDate,
 } from "global/utilities/ConvertDate";




const TransactionsTable = ({ initialTableColumns, transactionsData, rowKeyPrefix, loadingTransactions, uniqueFilterOptions, defaultFilters, fetchTransactionsByDateRange, handleSelectTransaction, displayDatePicker = true }) => {


    // Set boolean state for displaying quick checkbox filters
    const [toggleTableQuickFilters, setToggleQuickFilters] = useState(false);

    // Set filters state - needs passing to filters component and table component
    const [filters, setFilters] = useState({});

    // State needed for CreateCheckboxFilters
    const [selectedRows, setSelectedRows] = useState([]);
    
    // Your filter keys you want to persist
    const filterKeys = ['type'];

    // This object maps internal filter keys to user-friendly labels
    const filterLabels = {
        type: 'Transaction Type',
    };

    // Extract form-related state and functions from the `usePersistentFilters` custom hook  (for orders table filters)
    const {
        selectedFilters,
        toggleFilterValue,
        setFilterGroupAllOn,
        setFilterGroupAllOff,
        resetFilterGroup
    } = usePersistentFilters("transactionsTable", filterKeys, defaultFilters);



    useEffect(() => {

        // Whenever selectedFilters changes, apply them to the table filters
        setFilters((prev) => ({
            ...prev,
            ...selectedFilters,
        }));

    }, [selectedFilters]);


    // Check if any checkbox filters have been selected - so we can change the collapse/expand icon colour
    const anyFiltersSelected = Object.values(selectedFilters).some((selectedFilter) => selectedFilter.length > 0);



    // Set today's date
    const today = new Date().toISOString().split("T")[0];
    
    // This creates a Date object for today
    const previousMonth = new Date();

    // This moves the date back by one calendar month
    previousMonth.setMonth(previousMonth.getMonth() - 1);

    // This formats the date as YYYY-MM-DD
    const defaultStartDate = previousMonth.toISOString().split("T")[0];

    // Date range filter state
    const [searchFromDate, setSearchFromDate] = useState(defaultStartDate);
    const [searchToDate, setSearchToDate] = useState(today);

    // This stores the date range currently used to fetch orders
    const [activeOrderDateRange, setActiveOrderDateRange] = useState({
        startDate: defaultStartDate,
        endDate: today,
    });

    // Function to handle date filter submit button click
    const handleOrdersDatePickerSubmit = () => {

        // If there's no start or end date then return early
        if (!searchFromDate || !searchToDate) return;

        // Clear selected rows
        setSelectedRows([]);

        const formattedFromDate = formatDateFromMidnightString(searchFromDate);
        const formattedToDate = formatDateToMidnightString(searchToDate);

        // Otherwise fetch the orders by giving the dates to the api
        fetchTransactionsByDateRange(formattedFromDate, formattedToDate);

        // Store the dates that were just searched
        setActiveOrderDateRange({
            startDate: searchFromDate,
            endDate: searchToDate,
        });
            
    };

   

    // This applies one of the preset all-orders date ranges
    const handleOrdersDatePresetClick = (startDate, endDate = today) => {

        // Clear selected rows
        setSelectedRows([]);

        // Store the preset dates in the date pickers
        setSearchFromDate(startDate);
        setSearchToDate(endDate);

        // Format the preset dates for the API
        const formattedFromDate = formatDateFromMidnightString(startDate);
        const formattedToDate = formatDateToMidnightString(endDate);

        // Fetch the preset date range
        fetchTransactionsByDateRange(formattedFromDate, formattedToDate);

        // Store the dates that were just searched
        setActiveOrderDateRange({
            startDate: startDate,
            endDate: endDate,
        });

    };



    return (

        <div style={{ overflowX: 'auto', minWidth: '100%' }}>

            {displayDatePicker &&

                <div className="orders-date-tools">

                    <CreateDateFilterPickers
                        filterStartDate={searchFromDate}
                        setFilterStartDate={setSearchFromDate}
                        filterEndDate={searchToDate}
                        setFilterEndDate={setSearchToDate}
                        maxDate={today}
                        handleDateFilterSubmit={handleOrdersDatePickerSubmit}
                        buttonTitle="Search"
                        activeStartDate={activeOrderDateRange.startDate}
                        activeEndDate={activeOrderDateRange.endDate}
                        datePickerMessage={null}
                    />

                    <div className="orders-date-presets">

                        <button type="button" className="orders-date-presets__button" onClick={() => handleOrdersDatePresetClick(getDateDaysAgo(30))}>
                            30 days
                        </button>

                        <button type="button" className="orders-date-presets__button" onClick={() => handleOrdersDatePresetClick(getDateDaysAgo(60))}>
                            60 days
                        </button>

                        <button type="button" className="orders-date-presets__button" onClick={() => handleOrdersDatePresetClick(getDateDaysAgo(90))}>
                            90 days
                        </button>

                        <button type="button" className="orders-date-presets__button" onClick={() => handleOrdersDatePresetClick(getMonthToDateStartDate())}>
                            Month to date
                        </button>

                        <button type="button" className="orders-date-presets__button" onClick={() => handleOrdersDatePresetClick(getYearToDateStartDate())}>
                            Year to date
                        </button>

                    </div>

                </div>

            }

            <div className={"quick_filters_and_table " + ((Object.keys(uniqueFilterOptions).length > 0) ? "display-filters" : "hidden-filters")}>


                {(Object.keys(uniqueFilterOptions).length > 0) &&

                    <div className={"collapsable_quick_filters " + (toggleTableQuickFilters ? "table__quick_filters--open" : "table__quick_filters--closed")}>

                        {!toggleTableQuickFilters && (Object.keys(uniqueFilterOptions).length > 0) &&

                        <div className="collapse_quick_filters_button_banner" onClick={() => { setToggleQuickFilters((prev) => !prev) }}>

                            <button
                                title={toggleTableQuickFilters ? "Hide Quick Filters" : "Show Quick Filters"}
                                className={"collapse_quick_filters_button " + (anyFiltersSelected ? "highlight_quick_filters_icon" : "") }
                            >

                                {toggleTableQuickFilters ? "❮" : "🝖"}
                            
                            </button> 

                        </div>
                        }
         
                        {toggleTableQuickFilters &&

                            <div className="filter_groups">

                                <button
                                    onClick={() => { setToggleQuickFilters((prev) => !prev) }}
                                    title={"Hide Quick Filters"}
                                    className={"collapse_quick_filters_button collapse_quick_filters_button--hide " + (anyFiltersSelected ? "highlight_quick_filters_icon" : "") }
                                >

                                    ❮
                                
                                </button> 

                                <div className="left_checkbox_filters_div">

                                    {/* 
                                    <CreateCheckboxFilters
                                        uniqueFilterOptions={uniqueFilterOptions}
                                        selectedFilters={selectedFilters}
                                        filterLabels={filterLabels}
                                        clearFilterGroup={clearFilterGroup}
                                        toggleFilterValue={toggleFilterValue}
                                        setSelectedRows={setSelectedRows}
                                    />
                                    */}

                                    <CreateToggleFilters 

                                        // This contains all unique filter options grouped by key
                                        uniqueFilterOptions={uniqueFilterOptions}

                                        // This contains the currently included values for each filter group
                                        selectedFilters={selectedFilters}

                                        // This contains the default included values for each filter group
                                        defaultFilters={defaultFilters}

                                        // This contains the visible labels for each filter group
                                        filterLabels={filterLabels}

                                        // This toggles a single value on or off inside a filter group
                                        toggleFilterValue={toggleFilterValue}

                                        // This sets all values in a filter group to included
                                        setFilterGroupAllOn={setFilterGroupAllOn}

                                        // This sets all values in a filter group to excluded
                                        setFilterGroupAllOff={setFilterGroupAllOff}

                                        // This resets a filter group back to its default included values
                                        resetFilterGroup={resetFilterGroup}

                                        // So we can clear any selected table rows when changing filters
                                        setSelectedRows={setSelectedRows}

                                    />

                                </div>

                            </div>

                        }
                    
                    </div>

                }

                <div className="right_table_div">

                    <CustomisableTable 
                        tableData={transactionsData} 
                        initialTableColumns={initialTableColumns} 
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        handleSelectRow={handleSelectTransaction}
                        handleRowSingleClick={handleSelectTransaction} 
                        filterKeys={filterKeys} 
                        filters={filters} 
                        setFilters={setFilters} 
                        loadingData={loadingTransactions}
                        rowKeyPrefix={rowKeyPrefix}
                        noDataAvaiableMessage={ 

                            (!uniqueFilterOptions || Object.keys(uniqueFilterOptions).length === 0) ? 

                                "No transactions to display." 

                                : "There are no transactions matching your selected filters." 

                        }
                    />

                </div>

            </div>

        </div>



    )



};

export default TransactionsTable;
